<div class="h-full flex overflow-hidden bg-gray-50 dark:bg-gray-950">

  <!-- ══ PANEL IZQUIERDO ══════════════════════════════════════════════════════ -->
  <div class="flex-1 overflow-y-auto">
    <div class="max-w-4xl mx-auto py-6 px-6 space-y-5">

      <!-- Encabezado -->
      <div>
        <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Mapeo de campos del formulario</h2>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Relaciona cada campo de tu formulario con la propiedad correspondiente en el CRM.
        </p>
      </div>

      <!-- Stats bar -->
      @if (!loading && rows.length) {
        <div class="flex items-center gap-1.5 px-4 py-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 text-xs">
          <svg class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <span class="text-gray-600 dark:text-gray-400">Campos del formulario: <strong class="text-gray-900 dark:text-white">{{ totalFields }}</strong></span>
          <span class="text-gray-300 dark:text-gray-700">|</span>
          <span class="text-success-600 dark:text-success-400">Mapeados: <strong>{{ mappedCount }}</strong></span>
          <span class="text-gray-300 dark:text-gray-700">|</span>
          <span class="text-warning-600 dark:text-warning-400">Sin mapear: <strong>{{ unmappedCount }}</strong></span>
        </div>
      }

      <!-- Aviso: campos sin guardar -->
      @if (hasUnsavedFields) {
        <div class="flex items-start gap-2 p-3 rounded-xl bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/30 text-warning-800 dark:text-warning-300">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0L3.19 16a2 2 0 001.74 3z"/>
          </svg>
          <div class="text-xs">
            <p class="font-semibold">Tienes campos sin guardar.</p>
            <p class="mt-0.5">Vuelve a la pestaña <strong>Campos</strong> y guarda primero — solo los campos persistidos pueden mapearse al CRM.</p>
          </div>
        </div>
      }

      <!-- Avisos de campos obligatorios no cubiertos -->
      @if (missingRequiredWarnings.length) {
        <div class="flex items-start gap-2 p-3 rounded-xl bg-warning-50 dark:bg-warning-500/10 border border-warning-200 dark:border-warning-500/30 text-warning-800 dark:text-warning-300">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0L3.19 16a2 2 0 001.74 3z"/>
          </svg>
          <div class="text-xs">
            <p class="font-semibold">Faltan campos obligatorios por mapear.</p>
            <ul class="mt-1 space-y-0.5 list-disc list-inside">
              @for (msg of missingRequiredWarnings; track msg) {
                <li>{{ msg }}</li>
              }
            </ul>
          </div>
        </div>
      }

      <!-- ── Banner informativo: Lead via automatización ────────────────────── -->
      <div class="flex items-start gap-3 p-3.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/25">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-500 dark:text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
        <div class="text-xs text-brand-800 dark:text-brand-300 space-y-0.5">
          <p class="font-semibold">¿Quieres crear Leads desde este formulario?</p>
          <p class="leading-relaxed">Los leads no se mapean directamente aquí. Configura una <strong>automatización</strong> con el disparador <strong>"Formulario enviado"</strong> y la acción <strong>Crear registro → Lead</strong> para generarlos automáticamente con cada envío.</p>
        </div>
      </div>

      <!-- Estado: cargando / vacío / tabla -->
      @if (loading) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Cargando mapeos…</p>

      } @else if (mappableRows.length === 0) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Guarda primero los campos del formulario para poder mapearlos.</p>

      } @else {
        <!-- Tabla -->
        <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden">

          <!-- Cabecera -->
          <div class="grid grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,2fr)_100px_20px] gap-x-4 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            <span>Campo del formulario</span>
            <span>Tipo de campo</span>
            <span>Propiedad del CRM</span>
            <span>Estado</span>
            <span></span>
          </div>

          <!-- Filas -->
          @for (row of mappableRows; track row.field.id) {
            <button type="button" (click)="openRow(row)"
              class="w-full grid grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_minmax(0,2fr)_100px_20px] gap-x-4 items-center px-4 py-3 text-left transition-colors border-b border-gray-50 dark:border-gray-800/60 last:border-b-0"
              [class.bg-brand-50]="isActiveRow(row)"
              [class.dark:bg-brand-500/10]="isActiveRow(row)"
              [class.hover:bg-gray-50]="!isActiveRow(row)"
              [class.dark:hover:bg-gray-800/40]="!isActiveRow(row)">

              <!-- Campo: icono + nombre + requerido -->
              <div class="flex items-center gap-2.5 min-w-0">
                <div class="w-7 h-7 flex-shrink-0 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="FIELD_ICONS[row.field.fieldType]"/>
                  </svg>
                </div>
                <span class="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {{ row.field.label }}
                  @if (row.field.isRequired) { <span class="text-error-500 ml-0.5">*</span> }
                </span>
              </div>

              <!-- Tipo de campo -->
              <div class="flex items-center gap-1.5 min-w-0">
                <svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="FIELD_ICONS[row.field.fieldType]"/>
                </svg>
                <span class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ fieldTypeLabel(row.field.fieldType) }}</span>
              </div>

              <!-- Propiedad del CRM -->
              <div class="flex items-center gap-1.5 min-w-0">
                @if (row.mapping?.crmObjectType) {
                  <svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  <span class="text-sm text-gray-700 dark:text-gray-300 truncate">{{ propertyLabel(row) }}</span>
                } @else {
                  <span class="text-sm text-gray-400 dark:text-gray-500 italic">— Seleccionar propiedad —</span>
                }
              </div>

              <!-- Estado -->
              <div>
                @if (row.mapping?.crmObjectType) {
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400">
                    Mapeado
                  </span>
                } @else {
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400">
                    Sin mapear
                  </span>
                }
              </div>

              <!-- Chevron -->
              <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>

            </button>
          }
        </div>

        <!-- Nota: campos obligatorios -->
        @if (hasRequiredFields) {
          <p class="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span class="text-error-500">*</span> Los campos marcados con * son obligatorios en el formulario.
          </p>
        }
      }

    </div>
  </div>

  <!-- ══ PANEL DERECHO "Mapear campo" ════════════════════════════════════════ -->
  @if (activeRow) {
    <div class="w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">

      <!-- Header -->
      <div class="flex items-start justify-between gap-3 px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-10 h-10 flex-shrink-0 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="FIELD_ICONS[activeRow.field.fieldType]"/>
            </svg>
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ activeRow.field.label }}</p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ fieldTypeLabel(activeRow.field.fieldType) }}</p>
          </div>
        </div>
        <button type="button" (click)="closePanel()"
          class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mt-0.5">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body (scrollable) -->
      <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">

        <!-- Título de sección -->
        <div>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">Selecciona la propiedad del CRM</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
            Elige la propiedad donde se guardará la información de este campo.
          </p>
        </div>

        <!-- Buscador -->
        <div class="relative">
          <input
            type="text"
            [value]="search"
            (input)="search = $any($event.target).value"
            placeholder="Buscar propiedad…"
            class="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
          />
          <svg class="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>

        <!-- Tabs de entidad -->
        <div class="flex gap-0 border-b border-gray-200 dark:border-gray-700 -mx-4 px-4">
          @for (tab of entityTabs; track tab.value) {
            <button type="button" (click)="onTabChange(tab.value)"
              class="px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap border-b-2 -mb-px"
              [class.border-brand-500]="activeTab === tab.value"
              [class.text-brand-600]="activeTab === tab.value"
              [class.dark:text-brand-400]="activeTab === tab.value"
              [class.border-transparent]="activeTab !== tab.value"
              [class.text-gray-500]="activeTab !== tab.value"
              [class.dark:text-gray-400]="activeTab !== tab.value"
              [class.hover:text-gray-700]="activeTab !== tab.value"
              [class.dark:hover:text-gray-200]="activeTab !== tab.value">
              {{ tab.label }}
            </button>
          }
        </div>

        <!-- Nota contextual según el objeto seleccionado -->
        @if (activeTab === 'TICKET') {
          <div class="flex items-start gap-2 p-2.5 rounded-lg bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/25 text-xs text-sky-800 dark:text-sky-300">
            <svg class="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p class="leading-relaxed"><strong>Creación directa.</strong> Con cada envío se crea un ticket nuevo usando los campos mapeados. El campo <strong>Asunto</strong> es obligatorio para que el ticket se genere.</p>
          </div>
        } @else if (activeTab === 'CONTACT') {
          <div class="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
            <svg class="w-3.5 h-3.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p class="leading-relaxed">El contacto se busca por correo. Si no existe, se crea. Los campos mapeados se actualizan según la regla de actualización elegida.</p>
          </div>
        }

        <!-- Lista de propiedades -->
        @if (isLoadingCurrentTab) {
          <div class="py-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <span class="animate-spin inline-block w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full"></span>
            Cargando…
          </div>

        } @else if (filteredProperties.length === 0) {
          @if (hasIncompatibleType) {
            <div class="py-4 px-2 text-center space-y-1">
              <svg class="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
              </svg>
              <p class="text-xs font-medium text-gray-600 dark:text-gray-300">Sin propiedades compatibles</p>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
                Los campos de tipo <strong>{{ fieldTypeLabel(activeRow!.field.fieldType) }}</strong>
                no pueden mapearse a propiedades de este objeto.
              </p>
            </div>
          } @else {
            <p class="py-4 text-center text-xs text-gray-400 dark:text-gray-500">
              {{ search ? 'Sin resultados para "' + search + '"' : 'No hay propiedades disponibles para este objeto.' }}
            </p>
          }

        } @else {
          <div class="space-y-1">
            @for (prop of filteredProperties; track prop.key) {
              <button type="button" (click)="onPropertySelect(prop.key)"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors"
                [class.bg-brand-50]="isSelected(prop.key)"
                [class.dark:bg-brand-500/10]="isSelected(prop.key)"
                [class.hover:bg-gray-50]="!isSelected(prop.key)"
                [class.dark:hover:bg-gray-800]="!isSelected(prop.key)">

                <!-- Icono de tipo de propiedad -->
                <div class="w-7 h-7 flex-shrink-0 rounded-lg flex items-center justify-center"
                  [class.bg-brand-100]="isSelected(prop.key)"
                  [class.dark:bg-brand-500/20]="isSelected(prop.key)"
                  [class.bg-gray-100]="!isSelected(prop.key)"
                  [class.dark:bg-gray-800]="!isSelected(prop.key)">
                  <svg class="w-3.5 h-3.5"
                    [class.text-brand-600]="isSelected(prop.key)"
                    [class.dark:text-brand-400]="isSelected(prop.key)"
                    [class.text-gray-400]="!isSelected(prop.key)"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="crmPropIcon(prop.type)"/>
                  </svg>
                </div>

                <!-- Nombre y tipo -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{{ prop.label }}</p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{{ crmPropTypeLabel(prop.type) }}</p>
                </div>

                <!-- Radio indicator -->
                <div class="flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                  [class.border-brand-500]="isSelected(prop.key)"
                  [class.border-gray-300]="!isSelected(prop.key)"
                  [class.dark:border-gray-600]="!isSelected(prop.key)">
                  @if (isSelected(prop.key)) {
                    <div class="w-2 h-2 rounded-full bg-brand-500"></div>
                  }
                </div>

              </button>
            }
          </div>
        }

        <!-- Valor fijo desde catálogo (solo campos HIDDEN mapeados a propiedad FK) -->
        @if (showCatalogPicker) {
          <div class="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-1.5">
            <label class="block text-[11px] font-medium text-gray-500 dark:text-gray-400">Valor predeterminado</label>
            @if (catalogLoading) {
              <div class="flex items-center gap-1.5 py-1 text-[11px] text-gray-400 dark:text-gray-500">
                <span class="animate-spin inline-block w-3 h-3 border-2 border-gray-300 border-t-brand-500 rounded-full"></span>
                Cargando opciones…
              </div>
            } @else {
              <select
                (change)="pendingFixedValue = $any($event.target).value"
                class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 transition">
                <option value="" [selected]="!pendingFixedValue">— Seleccionar —</option>
                @for (opt of catalogOptions; track opt.id) {
                  <option [value]="opt.value" [selected]="opt.value === pendingFixedValue">{{ opt.label }}</option>
                }
              </select>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
                Valor que se enviará al CRM al procesar el formulario.
              </p>
            }
          </div>
        }

        <!-- Opciones avanzadas (colapsable): solo si hay propiedades compatibles -->
        <div class="border-t border-gray-100 dark:border-gray-800 pt-3" [class.hidden]="hasIncompatibleType">
          <button type="button" (click)="advancedOpen = !advancedOpen"
            class="w-full flex items-center justify-between py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            <span>Opciones avanzadas</span>
            <svg class="w-3.5 h-3.5 transition-transform" [class.rotate-180]="advancedOpen"
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          @if (advancedOpen) {
            <div class="mt-3 space-y-2">
              <label class="block text-[11px] text-gray-500 dark:text-gray-400">Regla de actualización</label>
              <select
                [value]="pendingUpdateRule"
                (change)="pendingUpdateRule = $any($event.target).value"
                class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 transition">
                @for (rule of updateRules; track rule.value) {
                  <option [value]="rule.value" [selected]="rule.value === pendingUpdateRule">{{ rule.label }}</option>
                }
              </select>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
                Controla qué pasa cuando el contacto ya tiene un valor guardado en esta propiedad.
              </p>
            </div>
          }
        </div>

        <!-- Quitar mapeo actual -->
        @if (activeRow.mapping?.crmObjectType) {
          <div class="pt-1">
            <button type="button" (click)="onRemoveMapping()" [disabled]="saving"
              class="text-xs text-error-500 hover:text-error-700 dark:hover:text-error-400 disabled:opacity-50 transition-colors">
              Quitar mapeo actual
            </button>
          </div>
        }

      </div>

      <!-- Footer -->
      <div class="flex-shrink-0 flex gap-2 px-4 py-4 border-t border-gray-100 dark:border-gray-800">
        <button type="button" (click)="closePanel()"
          class="flex-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          Cancelar
        </button>
        <button type="button" (click)="onSave()"
          [disabled]="!pendingStdField || saving || hasIncompatibleType"
          class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          @if (saving) {
            <span class="flex items-center justify-center gap-1.5">
              <span class="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full"></span>
              Guardando…
            </span>
          } @else {
            Guardar mapeo
          }
        </button>
      </div>

    </div>
  }

</div>
